import { Box, Image, ImageProps, Skeleton, Text } from "@caspeco/casper-ui-library";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ImageWithSkeletonProps extends ImageProps {
    src?: string;
    alt: string;
    skeletonWidth?: string;
    skeletonHeight?: string;
}

/**
 * A component that shows a skeleton while an image is loading.
 * Once the image is loaded, the skeleton is hidden and the image is shown.
 * If the image fails to load, a fallback message is displayed.
 */
const ImageWithSkeleton = ({
    src,
    alt,
    skeletonWidth,
    skeletonHeight,
    ...restProps
}: ImageWithSkeletonProps) => {
    // Which src the outcome belongs to, not whether one ever loaded, so a new src starts hidden
    // without needing an effect to reset it and cannot keep painting the previous image.
    const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);
    const [erroredSrc, setErroredSrc] = useState<string | undefined>(undefined);
    const { t } = useTranslation();

    if (!src) {
        return (
            <Box width={restProps.width} height={restProps.height}>
                <Text>{t("imageWithSkeleton.imageFailure")}</Text>
            </Box>
        );
    }

    const isLoaded = loadedSrc === src;
    const hasError = erroredSrc === src;

    const handleImageLoad = () => {
        setLoadedSrc(src);
    };

    const handleImageError = () => {
        setLoadedSrc(src);
        setErroredSrc(src);
    };

    // Use image dimensions for skeleton if no skeleton dimensions provided
    const skWidth = skeletonWidth !== undefined ? skeletonWidth : restProps.width;
    const skHeight = skeletonHeight !== undefined ? skeletonHeight : restProps.height;

    return (
        <Box position="relative">
            {!isLoaded && (
                <Skeleton
                    h={skHeight || "100%"}
                    w={skWidth || "100%"}
                    data-testid="image-skeleton"
                />
            )}

            {hasError ? (
                <Text>{t("imageWithSkeleton.imageFailure")}</Text>
            ) : (
                <Image
                    src={src}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    display={isLoaded ? "block" : "none"}
                    {...restProps}
                />
            )}
        </Box>
    );
};

export default ImageWithSkeleton;
